<template>
  <div class="min-h-screen" data-theme="violet">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="competitions" />

    <!-- Content -->
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="flex items-center justify-between mb-8">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('competitions.title') }}</h1>
        <button v-if="signedIn" @click="openCreateModal" class="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-800 transition">
          {{ t('competitions.create') }}
        </button>
      </div>

      <!-- Filters -->
      <div class="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div class="flex flex-wrap items-center gap-4">
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('competitions.filter_date') }}:</label>
            <input v-model="filterDateFrom" type="date"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">—</span>
            <input v-model="filterDateTo" type="date"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('competitions.running_type') }}:</label>
            <select v-model="filterType" class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">{{ t('competitions.filter_all') }}</option>
              <option value="road">{{ t('competitions.type_road') }}</option>
              <option value="trail">{{ t('competitions.type_trail') }}</option>
              <option value="cross">{{ t('competitions.type_cross') }}</option>
              <option value="stadium">{{ t('competitions.type_stadium') }}</option>
            </select>
          </div>

          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('competitions.distances') }}:</label>
            <input v-model="filterDistFrom" type="number" min="0" step="1" placeholder="min"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 w-16 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">—</span>
            <input v-model="filterDistTo" type="number" min="0" step="1" placeholder="max"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 w-16 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <span class="text-xs text-gray-400">km</span>
          </div>

          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-500">{{ t('competitions.filter_search') }}:</label>
            <input v-model="filterSearch" type="text" :placeholder="t('competitions.filter_search_placeholder')"
                   class="text-xs border border-gray-300 rounded-lg px-2 py-1.5 w-48 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <span class="text-xs text-gray-400 ml-auto">{{ filteredCompetitions.length }} / {{ competitions.length }}</span>
        </div>
      </div>

      <!-- Map -->
      <div v-show="competitionsWithCoords.length > 0" class="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden map-container">
        <div ref="mapContainer" style="height: 320px; width: 100%;"></div>
      </div>

      <!-- Flash -->
      <div v-if="flash" :class="['mb-4 px-4 py-3 rounded-lg text-sm', flash.type === 'success' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800']">
        {{ flash.message }}
      </div>

      <!-- List -->
      <div v-if="filteredCompetitions.length === 0" class="text-center py-12 text-gray-500">
        {{ t('competitions.no_competitions') }}
      </div>

      <div class="grid gap-4">
        <div v-for="comp in filteredCompetitions" :key="comp.id"
             @click="goToComp($event, comp)"
             :class="['rounded-xl shadow-sm border p-6 transition cursor-pointer', isPast(comp) ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200 hover:shadow-md']">
          <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div class="flex flex-col items-center sm:items-start sm:flex-row gap-4 flex-1">
              <img v-if="comp.logo_url" :src="comp.logo_url" class="h-24 rounded-lg object-contain border border-gray-200 shrink-0 self-center sm:self-start" />
              <div class="flex-1 min-w-0 text-center sm:text-left">
                <div class="flex items-center gap-2 mb-1">
                  <span class="text-sm font-medium text-blue-800">{{ formatDate(comp.date) }}</span>
                  <span class="text-sm text-blue-600">{{ formatTime(comp.date) }}</span>
                  <span v-if="comp.pending" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {{ t('competitions.pending') }}
                  </span>
                </div>
                <span class="text-lg font-semibold text-gray-900 block mb-1">{{ comp.name }}</span>
                <div class="flex flex-wrap items-center justify-center sm:justify-start gap-1.5">
                  <span v-if="comp.running_type" class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {{ t('competitions.type_' + comp.running_type) }}
                  </span>
                  <span v-for="d in (comp.distances || [])" :key="d.id" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                    {{ d.km }} km
                    <span v-if="d.elevation_gain" class="text-gray-400">↑{{ d.elevation_gain }}m</span>
                  </span>
                </div>
                <div v-if="comp.link || comp.results_link" class="mt-1 flex flex-wrap gap-2" @click.stop>
                  <a v-if="comp.link" :href="comp.link" target="_blank" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition">
                    {{ t('competitions.visit_link') }}
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                  </a>
                  <a v-if="comp.results_link" :href="comp.results_link" target="_blank" class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-700 rounded-lg hover:bg-green-800 transition">
                    {{ t('competitions.results') }}
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"/></svg>
                  </a>
                </div>
              </div>
            </div>

            <div v-if="canEdit(comp)" class="flex flex-col gap-2 ml-4" @click.stop>
              <button v-if="userAdmin && comp.pending" @click="approveCompetition(comp)" class="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition">
                {{ t('competitions.approve') }}
              </button>
              <button @click="openEditModal(comp)" class="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition">
                {{ t('competitions.edit') }}
              </button>
              <button @click="confirmDelete(comp)" class="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition">
                {{ t('competitions.delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <h2 class="text-lg font-bold text-gray-900 mb-6">
          {{ editing ? t('competitions.edit_competition') : t('competitions.new_competition') }}
        </h2>

        <div v-if="formErrors.length" class="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          <p v-for="error in formErrors" :key="error" class="text-sm text-red-700">{{ error }}</p>
        </div>

        <form @submit.prevent="submitForm" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.name') }}</label>
            <input v-model="form.name" type="text" required
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.date') }}</label>
            <input v-model="form.date" type="datetime-local" required
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.link') }}</label>
            <input v-model="form.link" type="url" placeholder="https://..."
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.results_link') }}</label>
            <input v-model="form.results_link" type="url" placeholder="https://..."
                   class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm" />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.description') }}</label>
            <textarea v-model="form.description" rows="3"
                      class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.running_type') }}</label>
            <select v-model="form.running_type" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm">
              <option value="">—</option>
              <option value="road">{{ t('competitions.type_road') }}</option>
              <option value="trail">{{ t('competitions.type_trail') }}</option>
              <option value="cross">{{ t('competitions.type_cross') }}</option>
              <option value="stadium">{{ t('competitions.type_stadium') }}</option>
            </select>
          </div>

          <!-- Distances -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <label class="block text-sm font-medium text-gray-700">{{ t('competitions.distances') }}</label>
              <button type="button" @click="addDistance" class="text-xs text-blue-700 hover:text-blue-900 font-medium">+ {{ t('competitions.add_distance') }}</button>
            </div>
            <div v-for="(d, idx) in form.distances" :key="idx" class="flex items-center gap-2 mb-2">
              <input v-model="d.km" type="number" step="0.1" min="0" :placeholder="t('competitions.km')" class="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              <span class="text-xs text-gray-400">km</span>
              <input v-model="d.elevation_gain" type="number" min="0" :placeholder="t('competitions.elevation')" class="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
              <span class="text-xs text-gray-400">↑m</span>
              <button type="button" @click="form.distances.splice(idx, 1)" class="text-red-400 hover:text-red-600 text-sm">&times;</button>
            </div>
          </div>

          <ImageUpload v-model="form.logo_url" :label="t('competitions.logo')" :upload-text="t('competitions.upload_logo')" :change-text="t('competitions.change_logo')" :remove-text="t('competitions.remove_logo')" :csrf-token="csrfToken" upload-type="competitions" />
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.pick_location') }}</label>
            <div ref="formMapContainer" style="height: 200px; width: 100%;" class="rounded-lg border border-gray-300"></div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.latitude') }}</label>
              <input v-model="form.latitude" type="number" step="0.0000001" readonly
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('competitions.longitude') }}</label>
              <input v-model="form.longitude" type="number" step="0.0000001" readonly
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm" />
            </div>
          </div>

          <div class="flex justify-end gap-3 pt-4">
            <button type="button" @click="closeModal"
                    class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
              {{ t('competitions.cancel') }}
            </button>
            <button type="submit" :disabled="submitting"
                    class="px-4 py-2 text-sm font-medium text-white bg-blue-700 rounded-lg hover:bg-blue-800 transition disabled:opacity-50">
              {{ submitting ? '...' : (editing ? t('competitions.save') : t('competitions.create')) }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="closeDeleteModal">
      <div class="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <h2 class="text-lg font-bold text-gray-900 mb-2">{{ t('competitions.confirm_delete') }}</h2>
        <p class="text-sm text-gray-600 mb-6">{{ t('competitions.confirm_delete_message') }}</p>
        <div class="flex justify-end gap-3">
          <button @click="closeDeleteModal" class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition">
            {{ t('competitions.cancel') }}
          </button>
          <button @click="deleteCompetition" :disabled="submitting"
                  class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50">
            {{ submitting ? '...' : t('competitions.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'
import ImageUpload from './ImageUpload.vue'
import L from 'leaflet'

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

const props = defineProps({
  signedIn: Boolean,
  userName: String,
  userId: Number,
  userAdmin: Boolean,
  competitions: Array,
  csrfToken: String
})

const { t, locale } = useI18n()

const showMenu = ref(false)
const logoSize = ref(120)
const comps = ref([...props.competitions])
const monthAgo = new Date()
monthAgo.setMonth(monthAgo.getMonth() - 1)
const filterDateFrom = ref(monthAgo.toISOString().slice(0, 10))
const filterDateTo = ref('')
const filterSearch = ref('')
const filterType = ref('')
const filterDistFrom = ref('')
const filterDistTo = ref('')
const showModal = ref(false)
const showDeleteModal = ref(false)
const editing = ref(false)
const editingId = ref(null)
const submitting = ref(false)
const formErrors = ref([])
const deleteTarget = ref(null)
const flash = ref(null)
const mapContainer = ref(null)
const formMapContainer = ref(null)
let map = null
let markers = []
let formMap = null
let formMarker = null

const form = reactive({ name: '', date: '', link: '', results_link: '', description: '', latitude: '', longitude: '', logo_url: '', running_type: '', distances: [] })

function addDistance() { form.distances.push({ id: null, km: '', elevation_gain: '' }) }

const filteredCompetitions = computed(() => {
  return comps.value.filter(c => {
    const d = new Date(c.date).toISOString().slice(0, 10)
    if (filterDateFrom.value && d < filterDateFrom.value) return false
    if (filterDateTo.value && d > filterDateTo.value) return false
    if (filterType.value && c.running_type !== filterType.value) return false
    if (filterDistFrom.value || filterDistTo.value) {
      const dists = (c.distances || []).map(x => parseFloat(x.km))
      if (dists.length === 0) return false
      const from = parseFloat(filterDistFrom.value) || 0
      const to = parseFloat(filterDistTo.value) || Infinity
      if (!dists.some(km => km >= from && km <= to)) return false
    }
    if (filterSearch.value) {
      const q = filterSearch.value.toLowerCase()
      if (!c.name.toLowerCase().includes(q) && !(c.description || '').toLowerCase().includes(q)) return false
    }
    return true
  })
})

const competitionsWithCoords = computed(() => filteredCompetitions.value.filter(c => c.latitude && c.longitude))

function isPast(comp) { return new Date(comp.date) < new Date() }
function goToComp(e, comp) { window.location.href = `/competitions/${comp.id}` }
function canEdit(comp) { return props.userAdmin || comp.user_id === props.userId }

async function approveCompetition(comp) {
  try {
    const res = await request(`/competitions/${comp.id}/approve`, 'PATCH')
    if (res.ok) {
      const data = await res.json()
      const idx = comps.value.findIndex(c => c.id === comp.id)
      if (idx !== -1) comps.value[idx] = data
      showFlash(t('competitions.approved'))
    }
  } catch { showFlash(t('competitions.error'), 'error') }
}

function formatDate(d) { return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) }
function formatTime(d) { return new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) }

function showFlash(message, type = 'success') {
  flash.value = { message, type }
  setTimeout(() => { flash.value = null }, 4000)
}

function initMap() {
  if (!mapContainer.value || competitionsWithCoords.value.length === 0) return
  if (map) { map.remove(); map = null }
  const first = competitionsWithCoords.value[0]
  map = L.map(mapContainer.value).setView([parseFloat(first.latitude), parseFloat(first.longitude)], 13)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
  }).addTo(map)
  setTimeout(() => map.invalidateSize(), 100)
  updateMarkers()
}

function updateMarkers() {
  if (!map) return
  markers.forEach(m => map.removeLayer(m))
  markers = []

  const greyIcon = L.icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] })
  const blueIcon = L.icon({ iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png', shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] })

  competitionsWithCoords.value.forEach(comp => {
    const past = isPast(comp)
    const marker = L.marker([parseFloat(comp.latitude), parseFloat(comp.longitude)], { icon: past ? greyIcon : blueIcon, opacity: past ? 0.5 : 1 }).addTo(map)
    const dateStr = formatDate(comp.date)
    let popup = `<div style="min-width:160px"><strong>${comp.name}</strong><br><span style="color:#666">${dateStr}</span>`
    if (comp.link) popup += `<br><a href="${comp.link}" target="_blank" style="color:#1d4ed8;font-size:12px">${t('competitions.visit_link')}</a>`
    popup += `<br><a href="${'/competitions/' + comp.id}" style="color:#1d4ed8;font-size:12px;font-weight:500">${t('competitions.details')}</a></div>`
    marker.bindPopup(popup)
    markers.push(marker)
  })

  if (markers.length > 1) {
    map.fitBounds(L.featureGroup(markers).getBounds().pad(0.1))
  }
}

function initFormMap() {
  setTimeout(() => {
    if (!formMapContainer.value) return
    if (formMap) { formMap.remove(); formMap = null; formMarker = null }
    const lat = parseFloat(form.latitude) || 47.0105
    const lng = parseFloat(form.longitude) || 28.8638
    formMap = L.map(formMapContainer.value).setView([lat, lng], 13)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap' }).addTo(formMap)
    if (form.latitude && form.longitude) formMarker = L.marker([lat, lng]).addTo(formMap)
    formMap.on('click', (e) => {
      form.latitude = e.latlng.lat.toFixed(7)
      form.longitude = e.latlng.lng.toFixed(7)
      if (formMarker) formMarker.setLatLng(e.latlng)
      else formMarker = L.marker(e.latlng).addTo(formMap)
    })
    formMap.invalidateSize()
  }, 100)
}

onMounted(() => {
  if (competitionsWithCoords.value.length > 0) initMap()
  const editId = new URLSearchParams(window.location.search).get('edit')
  if (editId) { const c = comps.value.find(x => x.id === parseInt(editId)); if (c) openEditModal(c) }
})

watch(competitionsWithCoords, () => {
  nextTick(() => {
    if (competitionsWithCoords.value.length > 0) { if (!map) initMap(); else updateMarkers() }
  })
})

function resetForm() { Object.assign(form, { name: '', date: '', link: '', results_link: '', description: '', latitude: '', longitude: '', logo_url: '', running_type: '', distances: [] }); formErrors.value = [] }

function openCreateModal() { editing.value = false; editingId.value = null; resetForm(); showModal.value = true; initFormMap() }

function openEditModal(comp) {
  editing.value = true; editingId.value = comp.id
  form.name = comp.name; form.date = new Date(comp.date).toISOString().slice(0, 16)
  form.link = comp.link || ''; form.results_link = comp.results_link || ''; form.description = comp.description || ''
  form.latitude = comp.latitude || ''; form.longitude = comp.longitude || ''; form.logo_url = comp.logo_url || ''
  form.running_type = comp.running_type || ''
  form.distances = (comp.distances || []).map(d => ({ id: d.id, km: d.km, elevation_gain: d.elevation_gain || '' }))
  formErrors.value = []; showModal.value = true; initFormMap()
}

function closeModal() { showModal.value = false }
function confirmDelete(comp) { deleteTarget.value = comp; showDeleteModal.value = true }
function closeDeleteModal() { showDeleteModal.value = false; deleteTarget.value = null }

async function request(url, method, body = null) {
  const opts = { method, headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': props.csrfToken, 'Accept': 'application/json' } }
  if (body) opts.body = JSON.stringify(body)
  return fetch(url, opts)
}

async function submitForm() {
  submitting.value = true; formErrors.value = []
  const payload = { competition: { name: form.name, date: form.date, link: form.link, results_link: form.results_link, description: form.description, latitude: form.latitude || null, longitude: form.longitude || null, logo_url: form.logo_url, running_type: form.running_type, distances: form.distances } }
  try {
    const res = editing.value
      ? await request(`/competitions/${editingId.value}`, 'PATCH', payload)
      : await request('/competitions', 'POST', payload)
    if (res.ok) {
      const data = await res.json()
      if (editing.value) { const idx = comps.value.findIndex(c => c.id === editingId.value); if (idx !== -1) comps.value[idx] = data; showFlash(t('competitions.updated')) }
      else { comps.value.unshift(data); showFlash(t('competitions.created_success')) }
      closeModal()
    } else { formErrors.value = (await res.json()).errors || [t('competitions.error')] }
  } catch { formErrors.value = [t('competitions.error')] }
  finally { submitting.value = false }
}

async function deleteCompetition() {
  submitting.value = true
  try {
    const res = await request(`/competitions/${deleteTarget.value.id}`, 'DELETE')
    if (res.ok) { comps.value = comps.value.filter(c => c.id !== deleteTarget.value.id); showFlash(t('competitions.deleted')); closeDeleteModal() }
  } catch { showFlash(t('competitions.error'), 'error') }
  finally { submitting.value = false }
}
</script>
